/**
 * The Google access token for this tab, kept in sessionStorage so a reload
 * doesn't force a new sign-in. Google's implicit flow can't renew a token
 * without the user, so a stored token is only used until shortly before it
 * expires.
 */

export const GOOGLE_TOKEN_STORAGE_KEY = "anthm.google.token";

/** Treat the token as expired this long before Google does. */
export const EXPIRY_MARGIN_MS = 5 * 60 * 1000;

export interface GoogleToken {
  accessToken: string;
  /** Absolute time (ms) when Google expires the token. */
  expiresAt: number;
}

export function isUsable(token: GoogleToken | null, now = Date.now()): token is GoogleToken {
  return token !== null && now < token.expiresAt - EXPIRY_MARGIN_MS;
}

export function toGoogleToken(accessToken: string, expiresInSeconds: number | undefined, now = Date.now()) {
  return { accessToken, expiresAt: now + (expiresInSeconds || 3600) * 1000 } satisfies GoogleToken;
}

export function saveToken(token: GoogleToken): void {
  sessionStorage.setItem(GOOGLE_TOKEN_STORAGE_KEY, JSON.stringify(token));
}

export function clearToken(): void {
  sessionStorage.removeItem(GOOGLE_TOKEN_STORAGE_KEY);
}

/** The stored token if it's still usable; drops expired or malformed entries. */
export function loadToken(now = Date.now()): GoogleToken | null {
  const raw = sessionStorage.getItem(GOOGLE_TOKEN_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<GoogleToken>;
    const token =
      typeof parsed.accessToken === "string" && typeof parsed.expiresAt === "number"
        ? { accessToken: parsed.accessToken, expiresAt: parsed.expiresAt }
        : null;
    if (isUsable(token, now)) return token;
  } catch {
    // Malformed entry: fall through and drop it.
  }
  clearToken();
  return null;
}
